import {
  IconBinaryTree,
  IconFileText,
  IconHome,
  IconSettings,
  IconShieldHalf,
  IconTools,
} from "@tabler/icons-react";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import type { MessageDescriptor } from "@lingui/core";

import { useGameStore } from "../../state/store";
import type { Screen } from "../../state/screen";
import { IconButton } from "../common/IconButton";
import { cx } from "../common/cx";
import styles from "./NavBar.module.css";

type NavScreen = Extract<Screen, "dashboard" | "contracts" | "skills" | "tools">;

interface NavItem {
  screen: NavScreen;
  label: MessageDescriptor;
  Icon: typeof IconHome;
}

const NAV_ITEMS: readonly NavItem[] = [
  { screen: "dashboard", label: msg`Главная`, Icon: IconHome },
  { screen: "contracts", label: msg`Контракты`, Icon: IconFileText },
  { screen: "skills", label: msg`Навыки`, Icon: IconBinaryTree },
  { screen: "tools", label: msg`Инструменты`, Icon: IconTools },
];

const SECTION_BY_SCREEN: Partial<Record<Screen, NavScreen>> = {
  dashboard: "dashboard",
  contracts: "contracts",
  contract_details: "contracts",
  workspace: "contracts",
  log_analysis: "contracts",
  network_map: "contracts",
  phishing: "contracts",
  report_builder: "contracts",
  results: "contracts",
  skills: "skills",
  tools: "tools",
};

export function NavBar(): JSX.Element {
  const { i18n } = useLingui();
  const currentScreen = useGameStore((state) => state.currentScreen);
  const navigateTo = useGameStore((state) => state.navigateTo);
  const activeSection = SECTION_BY_SCREEN[currentScreen];

  return (
    <nav className={styles.navbar} aria-label={i18n._(msg`Основная навигация`)}>
      <button type="button" className={styles.brand} onClick={() => navigateTo("dashboard")}>
        <IconShieldHalf size={20} className={styles.brandIcon} aria-hidden="true" />
        <span className={styles.brandStrong}>
          <Trans>BreachGuard</Trans>
        </span>
        <span className={styles.brandSub}>
          <Trans>Bureau</Trans>
        </span>
      </button>

      <div className={styles.links}>
        {NAV_ITEMS.map(({ screen, label, Icon }) => (
          <button
            key={screen}
            type="button"
            className={cx(styles.link, activeSection === screen && styles.active)}
            aria-current={activeSection === screen ? "page" : undefined}
            onClick={() => navigateTo(screen)}
          >
            <Icon size={18} aria-hidden="true" />
            {i18n._(label)}
          </button>
        ))}
      </div>

      <span className={styles.spacer} />

      <IconButton
        label={i18n._(msg`Настройки`)}
        icon={<IconSettings size={19} />}
        toggled={currentScreen === "settings"}
        onClick={() => navigateTo("settings")}
      />
    </nav>
  );
}
